import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import PpobService from 'service/PPOB/PpobService';
import { PpobTransactionCheckResponseData } from '../../domain/payload/response/PPOB/PpobResp';

interface PpobHistoryDetailModalProps {
  show: boolean;
  onClose: () => void;
  transactionId?: string | null;
}

const PpobHistoryDetailModal: React.FC<PpobHistoryDetailModalProps> = ({
  show,
  onClose,
  transactionId,
}) => {
  const { profile } = useSelector((state: any) => state.auth);
  const [detail, setDetail] = useState<PpobTransactionCheckResponseData | null>(null);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const ppobService = PpobService();

  const fetchDetail = async () => {
    if (!transactionId) return;

    const token = profile?.data?.token;
    if (!token) return;

    setLoading(true);
    setErrorMsg(null);
    try {
      const res = await ppobService.getTransactionDetail(token, transactionId);
      setDetail(res.data ?? null);
    } catch (err: any) {
      console.error('Error fetching PPOB transaction detail:', err);
      const msg = typeof err === 'string' ? err : err.message || 'Gagal memuat detail transaksi';
      setErrorMsg(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (show && transactionId) {
      fetchDetail();
    }
  }, [show, transactionId]);

  useEffect(() => {
    if (!show) {
      setDetail(null);
      setErrorMsg(null);
    }
  }, [show]);

  if (!show) return null;

  const formatCurrency = (val: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      maximumFractionDigits: 0,
    }).format(val || 0);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'SUCCESS':
        return <span className="badge bg-success">Sukses</span>;
      case 'PENDING':
        return <span className="badge bg-info text-dark">Diproses</span>;
      case 'WAITING_PAYMENT':
        return <span className="badge bg-warning text-dark">Menunggu Pembayaran</span>;
      case 'EXPIRED':
        return <span className="badge bg-secondary">Kedaluwarsa</span>;
      case 'FAILED':
        return <span className="badge bg-danger">Gagal</span>;
      case 'CANCELLED':
        return <span className="badge bg-danger">Dibatalkan</span>;
      default:
        return <span className="badge bg-secondary">{status}</span>;
    }
  };

  return (
    <div
      className="modal fade show"
      style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}
      onClick={onClose}
    >
      <div className="modal-dialog modal-lg modal-dialog-scrollable" onClick={(e) => e.stopPropagation()}>
        <div className="modal-content position-relative">
          {loading && (
            <div
              className="position-absolute w-100 h-100 d-flex justify-content-center align-items-center"
              style={{ backgroundColor: 'rgba(255,255,255,0.8)', zIndex: 9999 }}
            >
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          )}

          <div className="modal-header border-bottom">
            <h5 className="modal-title fw-bold">Detail Transaksi PPOB</h5>
          </div>

          <div className="modal-body p-4">
            {errorMsg ? (
              <div className="alert alert-danger mb-0">
                <i className="fa-solid fa-circle-exclamation me-2"></i>
                {errorMsg}
              </div>
            ) : detail ? (
              <div className="row g-3">
                {/* Header Summary */}
                <div className="col-12">
                  <div className="p-3 bg-light rounded border">
                    <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                      <div>
                        <small className="text-muted d-block">No. Transaksi</small>
                        <h5 className="fw-bold text-primary mb-1">{detail.transactionNo}</h5>
                        <small className="text-muted">ID: {detail.id}</small>
                      </div>
                      <div className="text-end">
                        <small className="text-muted d-block mb-1">Status</small>
                        {getStatusBadge(detail.status)}
                      </div>
                    </div>
                    <hr className="my-2" />
                    <div className="row g-2 mt-1">
                      <div className="col-6 col-md-6">
                        <small className="text-muted d-block">Waktu Transaksi</small>
                        <span className="fw-semibold text-dark">{detail.createdAt || '-'}</span>
                      </div>
                      <div className="col-6 col-md-6">
                        <small className="text-muted d-block">Source App</small>
                        <span className="badge bg-white text-dark border">
                          <i className="fa-solid fa-mobile-screen me-1 text-primary"></i>
                          {detail.sourceApp || '-'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {(detail.status === 'FAILED' || detail.status === 'EXPIRED') && (
                  <div className="col-12">
                    <div className="alert alert-danger mb-0 d-flex align-items-center">
                      <i className="fa-solid fa-triangle-exclamation me-2 fs-5"></i>
                      <div>
                        <strong className="d-block">Catatan Status:</strong>
                        <span>
                          {detail.status === 'EXPIRED'
                            ? 'Waktu pembayaran telah kedaluwarsa.'
                            : detail.externalData?.message?.id || detail.externalData?.message?.en || 'Transaksi dibatalkan.'}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Section: Customer Number & Serial Number */}
                <div className="col-12 col-md-6">
                  <div className="p-3 bg-light rounded border h-100">
                    <small className="text-muted d-block mb-1">Customer Number / No. Pelanggan</small>
                    <h5 className="fw-bold text-dark mb-0">{detail.customerNumber || '-'}</h5>
                  </div>
                </div>

                <div className="col-12 col-md-6">
                  <div className="p-3 bg-light rounded border h-100">
                    <small className="text-muted d-block mb-1">Serial Number / SN / Token</small>
                    <h5 className="fw-bold text-dark mb-0">{detail.serialNumber || '-'}</h5>
                  </div>
                </div>

                {/* Section: Product & Item Details */}
                <div className="col-12 col-md-6">
                  <small className="text-muted d-block mb-1">Produk</small>
                  <div className="d-flex align-items-center">
                    {detail.product?.iconUrl && (
                      <img
                        src={detail.product.iconUrl}
                        alt={detail.product.name}
                        className="me-2"
                        style={{ width: 28, height: 28, objectFit: 'contain' }}
                      />
                    )}
                    <div>
                      <div className="fw-bold">{detail.product?.name || '-'}</div>
                      <small className="text-muted">Kode: {detail.product?.code || '-'}</small>
                    </div>
                  </div>
                </div>

                <div className="col-12 col-md-6">
                  <small className="text-muted d-block mb-1">Item Produk</small>
                  <div>
                    <div className="fw-bold">{detail.productItem?.name || '-'}</div>
                    <small className="text-muted">Kode: {detail.productItem?.code || '-'}</small>
                  </div>
                </div>

                <div className="col-12"><hr className="my-2" /></div>

                {/* Section: User Details */}
                <div className="col-12 col-md-4">
                  <small className="text-muted d-block">Nama User</small>
                  <span className="fw-semibold">{detail.userName || '-'}</span>
                </div>
                <div className="col-12 col-md-4">
                  <small className="text-muted d-block">Email User</small>
                  <span className="fw-semibold">{detail.userEmail || '-'}</span>
                </div>
                <div className="col-12 col-md-4">
                  <small className="text-muted d-block">No. Phone User</small>
                  <span className="fw-semibold">{detail.userPhone || '-'}</span>
                </div>

                <div className="col-12"><hr className="my-2" /></div>

                {/* Section: Payment Breakdown & Method */}
                <div className="col-12 col-md-6">
                  <h6 className="fw-bold mb-2">Rincian Pembayaran</h6>
                  {detail.transactionDetail && detail.transactionDetail.length > 0 && (
                    <div className="mb-2">
                      {detail.transactionDetail.map((item, idx) => (
                        <div key={idx} className="d-flex justify-content-between align-items-center py-1 border-bottom">
                          <span className={item.isBold ? 'fw-bold' : 'text-muted'}>{item.label}</span>
                          <span className={item.isBold ? 'fw-bold' : ''}>{item.formattedValue}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="d-flex justify-content-between align-items-center pt-2">
                    <span className="fw-bold">Total Pembayaran</span>
                    <span className="fw-bold fs-5 text-primary">{formatCurrency(detail.totalPayment)}</span>
                  </div>
                </div>

                <div className="col-12 col-md-6">
                  <h6 className="fw-bold mb-2">Metode & Kode Pembayaran</h6>
                  {detail.paymentMethod ? (
                    <div className="p-3 bg-light rounded border d-flex align-items-center justify-content-between mb-2">
                      <div className="d-flex align-items-center">
                        {detail.paymentMethod.logoUrl && (
                          <img
                            src={detail.paymentMethod.logoUrl}
                            alt={detail.paymentMethod.name}
                            className="me-2"
                            style={{ width: 36, height: 36, objectFit: 'contain' }}
                          />
                        )}
                        <div>
                          <div className="fw-bold">{detail.paymentMethod.name}</div>
                          <small className="text-muted">{detail.paymentMethod.subtitle || detail.paymentMethod.category}</small>
                        </div>
                      </div>
                      <div className="text-end">
                        <small className="text-muted d-block">Kode Bayar</small>
                        {detail.paymentMethod?.category?.toUpperCase() === 'QRIS' ||
                          detail.paymentMethod?.code?.toUpperCase().includes('QRIS') ||
                          detail.paymentMethod?.name?.toUpperCase().includes('QRIS') ||
                          (detail.paymentCode && (detail.paymentCode.startsWith('000201') || detail.paymentCode.includes('some-random-qr-string'))) ? (
                          <span className="fw-bold text-dark">QRIS</span>
                        ) : (
                          <span className="fw-bold text-dark">{detail.paymentCode || '-'}</span>
                        )}
                      </div>
                    </div>
                  ) : (
                    <span className="text-muted">-</span>
                  )}
                </div>

                {/* Section: External Data Biller */}
                {detail.externalData && (
                  <>
                    <div className="col-12"><hr className="my-2" /></div>
                    <div className="col-12">
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <h6 className="fw-bold mb-0">Respon Biller</h6>
                      </div>

                      <div className="p-3 bg-light rounded border">
                        <div className="row g-3">
                          {(detail.externalData.message?.id || detail.externalData.message?.en) && (
                            <div className="col-12">
                              <small className="text-muted d-block">Pesan Biller</small>
                              <span className="fw-semibold text-dark">
                                {detail.externalData.message?.id}
                              </span>
                            </div>
                          )}
                          {detail.externalData.data.customerName && (
                            <div className="col-6 col-md-4">
                              <small className="text-muted d-block">Nama Pelanggan Biller</small>
                              <span className="fw-bold text-dark">{detail.externalData.data.customerName}</span>
                            </div>
                          )}
                          {detail.externalData.data.accountNumber && (
                            <div className="col-6 col-md-4">
                              <small className="text-muted d-block">Account Number / No. Tujuan</small>
                              <span className="fw-bold text-dark">{detail.externalData.data.accountNumber}</span>
                            </div>
                          )}
                          {detail.externalData.data.refNumber && (
                            <div className="col-6 col-md-4">
                              <small className="text-muted d-block">Ref Number / Reff ID</small>
                              <span className="fw-bold text-dark">{detail.externalData.data.refNumber}</span>
                            </div>
                          )}
                          {detail.externalData.data.token && (
                            <div className="col-6 col-md-4">
                              <small className="text-muted d-block">Token PLN / Serial Number</small>
                              <span className="fw-bold text-primary">{detail.externalData.data.token}</span>
                            </div>
                          )}
                          {detail.externalData.data.denom && (
                            <div className="col-6 col-md-4">
                              <small className="text-muted d-block">Denom</small>
                              <span className="fw-semibold text-dark">{detail.externalData.data.denom}</span>
                            </div>
                          )}
                          {detail.externalData.data.productName && (
                            <div className="col-6 col-md-4">
                              <small className="text-muted d-block">Nama Produk Biller</small>
                              <span className="fw-semibold text-dark">{detail.externalData.data.productName}</span>
                            </div>
                          )}
                          {detail.externalData.data.description && (
                            <div className="col-12">
                              <small className="text-muted d-block">Deskripsi / Keterangan Tagihan</small>
                              <span className="fw-semibold text-dark">{detail.externalData.data.description}</span>
                            </div>
                          )}
                        </div>

                        {((detail.externalData.data.customerDetails && detail.externalData.data.customerDetails.length > 0) ||
                          (detail.externalData.data.productDetails && detail.externalData.data.productDetails.length > 0)) && (
                            <div className="mt-3 pt-2 border-top">
                              <small className="text-muted fw-bold d-block mb-2">Rincian Tambahan Biller:</small>
                              <div className="row g-2">
                                {detail.externalData.data.customerDetails?.map((item, idx) => (
                                  <div key={`cust-${idx}`} className="col-6 col-md-4">
                                    <small className="text-muted d-block">{item.key}</small>
                                    <span className="fw-semibold text-dark">{String(item.value)}</span>
                                  </div>
                                ))}
                                {detail.externalData.data.productDetails?.map((item, idx) => (
                                  <div key={`prod-${idx}`} className="col-6 col-md-4">
                                    <small className="text-muted d-block">{item.key}</small>
                                    <span className="fw-semibold text-dark">{String(item.value)}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            ) : (
              !loading && (
                <div className="text-center py-4">
                  <p className="text-muted mb-0">Detail transaksi tidak ditemukan</p>
                </div>
              )
            )}
          </div>

          <div className="modal-footer border-top">
            <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={loading}>
              Tutup
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PpobHistoryDetailModal;
